import { createColumnHelper } from '@tanstack/react-table'
import { IYieldCompounding } from '~/@api/generated-api-types-project/Api'
import { formatAmount } from '~/@lib/utils/number.utils'
import { minMaxFilter } from '~/@pages_investing/projects/_projects_filtering/filtering.utils'
import {
  YieldPerAnnumFilterFn,
  YieldPerAnnumSortingFn,
} from '~/@pages_investing/projects/_projects_filtering/filtering.utils'
import { TextTableHead } from '~/@mid_ui/typography/TextTableHead'
import { TextTableCell } from '~/@mid_ui/typography/TextTableCell'
import { StageLabel } from '~/@mid_ui/typography/StageLabel'

import { projectTooltipByType } from '../_projects_ui/project-info-tooltips/projectTooltipByType'
import { ProjectYieldText } from '../_projects_ui/project-yield-text/ProjectYieldText'
import { ProjectPreviewImageAndPopupCell } from '../_projects_ui/project-preview-and-label/ProjectPreviewImageAndPopupCell'
import { ProjectCellContainer } from '../_projects_ui/project-cell-container/ProjectCellContainer'
import type { ProjectsItem } from '../_project_types/project.types'
import type { ProjectLoanPurpose } from '../_project_types/projectLoanPurpose'
import type { ProjectCollateralSubcategory } from '../_project_types/projectCollateralSubCategory'
import { getProjectLoanPurposeLabel } from '../_projects_ui/project-loan-purpose/labelByProjectLoanPurpose'
import { transformSecurity } from '~/@lib/utils/riskScoreMapper'

const columnHelper = createColumnHelper<ProjectsItem>()

const hiddenColumns = [
  columnHelper.accessor('amountTarget', {
    id: 'amountTarget',
    filterFn: minMaxFilter,
  }),
  columnHelper.accessor('paymentTypeId', {
    id: 'paymentTypeId',
    filterFn: (row, columnId, filterValues: unknown[]) => {
      if (!filterValues.length) return true

      return filterValues.includes(row.getValue(columnId))
    },
  }),
]

const columns = [
  columnHelper.accessor('loanPurpose', {
    id: 'loanPurpose',
    header: () => (
      <ProjectCellContainer>
        <TextTableHead>Účel úveru</TextTableHead>
        {projectTooltipByType.loanPurpose}
      </ProjectCellContainer>
    ),
    cell: (props) => {
      const project = props.row.original as unknown as ProjectsItem

      return (
        <div className="flex gap-1">
          <TextTableCell className="flex flex-col items-start justify-center">
            <p>
              {project.loanPurpose != null
                ? getProjectLoanPurposeLabel(project.loanPurpose)
                : '-'}
            </p>
            <p className="text-sm  text-accent underline">
              {props.row.original.businessId}
            </p>
          </TextTableCell>
        </div>
      )
    },
    filterFn: (row, columnId, filterValue: ProjectLoanPurpose[]) => {
      if (!filterValue.length) return true

      return filterValue.includes(row.getValue(columnId))
    },
  }),
  columnHelper.accessor('collateralInfo.collateralSubcategory', {
    id: 'collateralSubcategory',
    header: () => (
      <ProjectCellContainer>
        <TextTableHead>Zaistenie</TextTableHead>
        {projectTooltipByType.category}
      </ProjectCellContainer>
    ),
    cell: ({ row: { original: project } }) => {
      return (
        <TextTableCell className="flex flex-row gap-2.5">
          <ProjectPreviewImageAndPopupCell project={project} />
        </TextTableCell>
      )
    },
    filterFn: (row, columnId, filterValue: ProjectCollateralSubcategory[]) => {
      if (!filterValue.length) return true

      return filterValue.includes(row.getValue(columnId))
    },
  }),
  columnHelper.accessor('yieldPerAnnum', {
    id: 'interestRate',
    header: () => (
      <ProjectCellContainer>
        <TextTableHead>
          Úrok ako <br /> predpokladaný výnos
        </TextTableHead>
        {projectTooltipByType.yield}
      </ProjectCellContainer>
    ),
    cell: (info) => {
      const projectInfo = info.row.original
      return (
        <TextTableCell>
          <ProjectYieldText
            _calculatedValues={projectInfo._calculatedValues}
            isYieldCompoundingAnnually={
              projectInfo.yieldCompounding === IYieldCompounding.Annually
            }
            yieldIconArrowColor="var(--color-primary)"
            yieldPercentColor="var(--color-green-dark)"
            yieldIconSize={22}
            className="text-green-dark"
          />
        </TextTableCell>
      )
    },
    sortingFn: YieldPerAnnumSortingFn,
    filterFn: YieldPerAnnumFilterFn,
  }),
  columnHelper.accessor('amountCurrent', {
    id: 'totalInvestedAmount',
    header: () => (
      <ProjectCellContainer>
        <div className="flex items-center justify-start gap-2">
          <TextTableHead>Zainvestované</TextTableHead>
          <StageLabel className="mb-1">Tranža</StageLabel>
        </div>
        {projectTooltipByType.investedAmount}
      </ProjectCellContainer>
    ),
    cell: (info) => {
      const value = info.getValue()

      return (
        <div className="flex items-center gap-2">
          <TextTableCell>{formatAmount(value)}</TextTableCell>
          {info.row.original.stageNumber && (
            <StageLabel>{info.row.original.stageNumber}.</StageLabel>
          )}
        </div>
      )
    },
  }),
  columnHelper.accessor('totalPaidAmount', {
    id: 'totalPaidAmount',
    header: () => (
      <ProjectCellContainer>
        <TextTableHead>
          Bolo <br /> vyplatené
        </TextTableHead>
        {projectTooltipByType.totalAmountPaid}
      </ProjectCellContainer>
    ),
    cell: (info) => (
      <TextTableCell>{formatAmount(info.getValue())}</TextTableCell>
    ),
  }),
  columnHelper.accessor('totalAmountToBePaid', {
    id: 'totalAmountToBePaid',
    header: () => (
      <ProjectCellContainer>
        <TextTableHead>
          Bude <br /> vyplatené
        </TextTableHead>
        {projectTooltipByType.totalAmountToBePaid}
      </ProjectCellContainer>
    ),
    cell: (info) => (
      <TextTableCell>{formatAmount(info.getValue())}</TextTableCell>
    ),
  }),
  columnHelper.accessor('ltlPercentage', {
    id: 'ltlPercentage',
    header: () => (
      <ProjectCellContainer>
        <TextTableHead>
          &nbsp;
          <br />
          LTL
        </TextTableHead>
        {projectTooltipByType.ltl}
      </ProjectCellContainer>
    ),
    cell: (info) => {
      const value = info.getValue()
      return <TextTableCell>{value ? `${value} %` : '-'}</TextTableCell>
    },
    filterFn: minMaxFilter,
  }),
  columnHelper.accessor('riskScore', {
    id: 'riskScore',
    header: () => (
      <ProjectCellContainer>
        <TextTableHead>
          Skóre <br /> zabezpečenia
        </TextTableHead>
        {projectTooltipByType.riskScore}
      </ProjectCellContainer>
    ),
    cell: (info) => {
      const riskScore = info.getValue()
      return (
        <TextTableCell>
          {riskScore != null ? transformSecurity(riskScore) : '-'}
        </TextTableCell>
      )
    },
    filterFn: minMaxFilter,
  }),
  columnHelper.accessor('payoutDate', {
    id: 'payoutDate',
    header: () => (
      <ProjectCellContainer>
        <TextTableHead>
          Dátum <br /> vyplatenia
        </TextTableHead>
        {projectTooltipByType.payoutDate}
      </ProjectCellContainer>
    ),
    cell: (info) => {
      const value = info.getValue()
      return (
        <TextTableCell>
          {value
            ? new Intl.DateTimeFormat('sk-SK').format(new Date(value))
            : '-'}
        </TextTableCell>
      )
    },
  }),
  ...hiddenColumns,
]

export { hiddenColumns, columns }
